import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { GridCellParams } from '@/global/components/DataGrid/utils/types'
import { Switch } from '@/global/components/Switch'
import { groupBy } from '@acme/common'
import { SelectEmpresa } from 'global/components/Selects'
import ContentGuide from 'global/guides/ContentGuide'
import TabbedDialog from 'global/guides/Dialog/TabbedDialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { useSupervisoresOutletContext } from 'pages/Supervisor'
import { useParams } from 'react-router-dom'
import {
    centroCusto as centroCusto_api,
    empresas as empresas_api,
    local as local_api,
    Supervisor,
    supervisor as supervisor_api,
} from 'service/api'
import ApiService from 'service/api/api.service'
import { toast } from 'sonner'
import { useStore } from 'store'
import ArvoreCentroCusto from '../ArvoreCentroCusto'

export default function AssociarSupervisor() {
    const { id } = useParams()
    const { data } = useSupervisoresOutletContext()

    const supervisor = data.find(supervisor => supervisor.id === Number(id))

    if (!supervisor) return null

    return (
        <TabbedDialog.Root
            open={hasPermission(['update', 'Supervisores'])}
            tabs={[
                {
                    label: 'Empresas',
                    name: 'empresas',
                    content: <TabEmpresas supervisor={supervisor} />,
                },
                {
                    label: 'Centros de custo',
                    name: 'centros-custo',
                    content: <TabCentrosCusto supervisor={supervisor} />,
                },
                {
                    label: 'Locais',
                    name: 'locais',
                    content: <TabLocais supervisor={supervisor} />,
                },
            ]}
        >
            <TabbedDialog.Title>Associar supervisor</TabbedDialog.Title>
            <TabbedDialog.Content dividers />
            <TabbedDialog.Actions>
                <TabbedDialog.Actions.SecondaryButton />
            </TabbedDialog.Actions>
        </TabbedDialog.Root>
    )
}

interface ITabProps {
    supervisor: Supervisor
}

const datagridprops = {
    ToolbarProps: { hideToolbar: true },
    hideFooter: true,
    density: 'compact' as const,
}

function TabEmpresas({ supervisor }: ITabProps) {
    const { data, isLoading } = empresas_api.useFetch()
    const empresasAssociadas = supervisor.SupervisorEmpresas ?? []

    return (
        <ContentGuide.DataGrid
            {...datagridprops}
            loading={isLoading}
            columns={[
                {
                    ...optionsColumn,
                    field: 'id',
                    headerName: 'Associar',
                    width: 80,
                    align: 'center',
                    renderCell: params => (
                        <SwitchAssociado
                            params={params}
                            idsField="empresaIds"
                            mutationApi={supervisor_api.empresa}
                            supervisorId={supervisor.id}
                        />
                    ),
                },
                {
                    ...optionsColumn,
                    field: 'nomeFantasia',
                    headerName: 'Empresa',
                    flex: 1,
                },
            ]}
            rows={data?.map(empresa => {
                const relacao = empresasAssociadas.find(
                    item => item.empresaId == empresa.id,
                )
                return {
                    ...empresa,
                    associado: !!relacao,
                    relacaoId: relacao?.id,
                }
            })}
        />
    )
}

function TabCentrosCusto({ supervisor }: ITabProps) {
    const empresaId = useStore(state => state.companyId)
    const { data } = centroCusto_api.useFetch({ queryParams: { empresaId } })
    const centrosEmpresas = groupBy(data, 'empresaId')

    return (
        <div className="grid grid-rows-[min-content_1fr] gap-4 size-full max-h-full">
            <SelectEmpresa width="100%" size="medium" />
            <ArvoreCentroCusto
                fetchedData={centrosEmpresas[empresaId]}
                supervisor={supervisor}
            />
        </div>
    )
}

function TabLocais({ supervisor }: ITabProps) {
    const { data, isLoading } = local_api.all.useFetch()
    const { data: empresas } = empresas_api.useFetch()
    const locaisAssociados = supervisor.SupervisorLocal ?? []

    return (
        <ContentGuide.DataGrid
            {...datagridprops}
            loading={isLoading}
            columns={[
                {
                    ...optionsColumn,
                    field: 'id',
                    headerName: 'Associar',
                    width: 80,
                    align: 'center',
                    renderCell: params => (
                        <SwitchAssociado
                            params={params}
                            idsField="localIds"
                            mutationApi={supervisor_api.local}
                            supervisorId={supervisor.id}
                        />
                    ),
                },
                {
                    ...optionsColumn,
                    field: 'nomeLocal',
                    headerName: 'Local',
                    flex: 1,
                },
                {
                    ...optionsColumn,
                    field: 'empresaId',
                    headerName: 'Empresa',
                    flex: 0.6,
                    valueGetter: params =>
                        empresas?.find(
                            empresa => empresa.id === params.row.empresaId,
                        )?.nomeFantasia,
                },
            ]}
            rows={data?.map(local => {
                const relacao = locaisAssociados.find(
                    item => item.localId == local.id,
                )
                return {
                    ...local,
                    associado: !!relacao,
                    relacaoId: relacao?.id,
                }
            })}
        />
    )
}

interface IPropsSwitch {
    params: GridCellParams<{ associado: boolean; id: number }>
    mutationApi: ApiService<any>
    supervisorId: number
    idsField: string
}

function SwitchAssociado({
    params,
    mutationApi,
    idsField,
    supervisorId,
}: IPropsSwitch) {
    const checked = params.row.associado
    const options = {
        invalidateQuery: true,
        onSuccess: () => {
            supervisor_api.invalidateQueries()
            toast.success('As alterações foram salvas')
        },
        onError: (e: any) => {
            toast.error(e.message)
        },
    }
    const { mutateAsync: associar, isLoading } =
        mutationApi.mutations.useCreate(options)
    const { mutateAsync: desassociar, isLoading: isDesassociando } =
        mutationApi.mutations.useDelete(options)

    const handleUpdate = () =>
        checked
            ? desassociar(params.row.relacaoId, {})
            : associar(
                  {
                      supervisorId,
                      [idsField]: [params.row.id],
                  },
                  {
                      onError: err => {
                          toast.error(err.message)
                      },
                  },
              )

    return (
        <Switch
            checked={checked}
            disabled={isLoading || isDesassociando}
            onChange={handleUpdate}
        />
    )
}
